import { MediaCoverView } from '@euler/app/components/media/MediaCoverView';
import { Typography } from '@euler/app/components/typography';
import { EmptyPlaceholder } from '@euler/app/tv/components/EmptyPlaceholder';
import { MediaList } from '@euler/app/tv/components/MediaList';
import { usePresentationTheme } from '@euler/app/tv/theme';
import { useContainerLayout } from '@euler/components/layout/LayoutProvider';
import { computeGridLayoutItemSize } from '@euler/functions/layoutMetrics';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { InventoryQuery } from '@euler/functions/useInventory';
import { MediaObject } from '@euler/model';
import { SiteInspectionType } from '@euler/model/enum';
import {
  PreInspection,
  TaskMedia,
  taskMediaToMediaObject,
} from '@euler/model/task-detail';
import { MotiView } from 'moti';
import { memo, useCallback, useMemo } from 'react';
import {
  Platform,
  StyleProp,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';

const kCellMargin = 40;
const kMaxCellWidth = 208;

export const FacadeInspectionResults = memo(
  ({
    preInspection,
    inventoryQuery,
    style,
  }: {
    preInspection: PreInspection;
    inventoryQuery: InventoryQuery;
    style?: StyleProp<ViewStyle>;
  }) => {
    const { rpx } = useResponsiveContext();
    const { width } = useContainerLayout();
    const cellMargin = rpx(kCellMargin);
    const columns = Math.floor(width / (rpx(kMaxCellWidth) + cellMargin));
    const [cellWidth, cellHeight] = computeGridLayoutItemSize({
      numberOfColumns: columns + 0.25,
      gridWidth: width,
      cellMargin: { left: 0, top: 0, bottom: 0, right: cellMargin },
      aspectRatio: 208 / 156,
      gridPadding: 0,
    });
    const medias = useMemo(() => {
      const list: TaskMedia[] = [];
      for (const inspectedSite of preInspection.sites) {
        if (inspectedSite.inspectionType !== SiteInspectionType.Facade) {
          continue;
        }
        const site = inventoryQuery.getSiteById(inspectedSite.siteId);
        for (const inspectedItem of inspectedSite.items) {
          for (const media of inspectedItem.medias) {
            const obj: TaskMedia = { ...media };
            if (!obj.title) {
              obj.title = site?.name;
            }
            if (!obj.subTitle) {
              const item = site?.checkItems.find(
                x => x.id === inspectedItem.itemId,
              );
              obj.subTitle = item?.name ?? inspectedItem.name;
            }
            list.push(obj);
          }
        }
      }
      return list;
    }, [inventoryQuery, preInspection.sites]);

    if (!medias.length) {
      return (
        <EmptyPlaceholder
          style={style}
          text="暂无相关车辆外观检测结果"
          iconSize={rpx(120)}
          textStyle={{
            fontSize: rpx(20),
            lineHeight: rpx(44),
            marginTop: rpx(15),
          }}
        />
      );
    }

    return (
      <View style={style}>
        <MediaList
          data={medias}
          mapToMediaObject={taskMediaToMediaObject}
          keyExtractor={x => x.cid}
          horizontal
          showsHorizontalScrollIndicator={Platform.OS === 'web'}
          renderItem={({ item, media, index, onPress }) => (
            <Cell
              item={item}
              media={media}
              index={index}
              onPress={onPress}
              css={`
                width: ${cellWidth}px;
                margin-right: ${cellMargin}px;
              `}
              mediaStyle={{
                width: cellWidth,
                height: cellHeight,
              }}
            />
          )}
        />
      </View>
    );
  },
);

const Cell = memo(
  ({
    media,
    index,
    style,
    mediaStyle,
    onPress,
  }: {
    item: TaskMedia;
    media: MediaObject;
    index: number;
    style?: StyleProp<ViewStyle>;
    mediaStyle?: StyleProp<ViewStyle>;
    onPress?: (media: MediaObject) => void;
  }) => {
    const theme = usePresentationTheme();
    const { rpx } = useResponsiveContext();
    const handlePress = useCallback(() => {
      onPress?.(media);
    }, [media, onPress]);
    return (
      <MotiView
        from={{
          opacity: 0,
          translateX: 100,
        }}
        animate={{
          opacity: 1,
          translateX: 0,
        }}
        transition={{
          type: 'timing',
          duration: 350,
        }}
        delay={Math.min(1500, index * 50)}
        style={style}
      >
        <MediaCoverView
          media={media}
          TouchableComponent={TouchableOpacity}
          onPress={handlePress}
          activityIndicatorSize={rpx(40)}
          noPlaceholder={media.type === 'image' && media.url.includes('#')}
          containerStyle={{
            borderRadius: rpx(16),
            overflow: 'hidden',
          }}
          style={mediaStyle}
        />
        <View
          pointerEvents="none"
          css={`
            justify-content: center;
            margin-top: ${rpx(12)}px;
          `}
        >
          {media.title ? (
            <Typography.Label
              regular
              size={rpx(18)}
              color={theme.task.media.titleColor}
            >
              {media.subTitle
                ? `${media.title}: ${media.subTitle}`
                : media.title}
            </Typography.Label>
          ) : null}
        </View>
      </MotiView>
    );
  },
);
